import React, { useEffect, useMemo, useState } from 'react';
import styles from './Statistics.module.scss';
import Loader from '../../Molecules/Loader/Loader';
import NoDataComponent from '../../Molecules/Custom/NoDataComponent/NoDataComponent';
import NewsTopHeader from '../../Molecules/TableTopHeader/NewsTopHeader';
import StatisticsCard from './components/statistic-card/StatisticsCard';

import {
  useFetchGraduationStatistics,
  useDeleteGraduationStatistics,
} from '../../../Hooks/Statistics';

import CreateCountryModal from './components/create/CreateStatisticsModal';
import DeletePopup from '../../Molecules/Custom/DeletePopup';
import ManageCandidatesBatchesModal from './components/manageBatches/ManageCandidatesBatchesModal';

const useDebouncedValue = (value, delay = 300) => {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
};

const normalize = (v) => String(v ?? '').trim();

const StatisticsPage = ({ countries = [] }) => {
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search, 300);

  const {
    data: stats,
    refetch: refetchStats,
    isLoading,
  } = useFetchGraduationStatistics(debouncedSearch);

  const { mutate: deleteStat, isLoading: isDeleting } =
    useDeleteGraduationStatistics();

  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('create');
  const [selectedItem, setSelectedItem] = useState(null);

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteItem, setDeleteItem] = useState(null);
  const [candidatesModalOpen, setCandidatesModalOpen] = useState(false);

  const openCandidatesModal = () => setCandidatesModalOpen(true);
  const closeCandidatesModal = () => setCandidatesModalOpen(false);
  const handleSaveCandidatesBatches = async (payload) => {
    console.log('Candidates/Batches payload:', payload);
  };
  useEffect(() => {
    refetchStats?.();
  }, [debouncedSearch, refetchStats]);

  const list = useMemo(() => {
    const rawList = Array.isArray(stats?.data) ? stats.data : [];
    const s = normalize(debouncedSearch).toLowerCase();
    if (!s) return rawList;

    return rawList.filter((item) => {
      const haystack = [
        item?.countryName_AR,
        item?.countryName_EN,
        item?.countryCode,
        item?.iso2,
        item?.iso3,
      ]
        .map(normalize)
        .join(' ')
        .toLowerCase();

      return haystack.includes(s);
    });
  }, [stats?.data, debouncedSearch]);

  const openCreate = () => {
    setSelectedItem(null);
    setModalMode('create');
    setModalOpen(true);
  };

  const openEdit = (item) => {
    setSelectedItem(item);
    setModalMode('edit');
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setSelectedItem(null);
    setModalMode('create');
  };

  const openDelete = (item) => {
    setDeleteItem(item);
    setDeleteOpen(true);
  };

  const closeDelete = () => {
    setDeleteOpen(false);
    setDeleteItem(null);
  };

  const handleDelete = (item) => {
    const id =
      item?.graduationStatisticsId ?? item?.id ?? item?.statisticsId ?? null;

    if (!id) return;

    deleteStat(id, {
      onSuccess: () => {
        closeDelete();
        refetchStats?.();
      },
      onError: (e) => console.error(e),
    });
  };

  return (
    <div>
      <NewsTopHeader
        handleNewClick={openCreate}
        type='statistics'
        search={search}
        setSearch={setSearch}
        handleCandidatesClick={openCandidatesModal}
      />
      <div className={styles['table-container']}>
        {isLoading ? (
          <Loader />
        ) : list.length > 0 ? (
          <div className={styles['media-box-grid']}>
            {list.map((item) => (
              <StatisticsCard
                key={
                  item?.graduationStatisticsId ?? item?.id ?? item?.countryId
                }
                data={item}
                onEdit={openEdit}
                onDelete={openDelete}
              />
            ))}
          </div>
        ) : (
          <NoDataComponent />
        )}
      </div>
      {modalOpen && (
        <CreateCountryModal
          mode={modalMode}
          selectedItem={selectedItem}
          handleOnClose={closeModal}
          refetch={refetchStats}
          countries={countries}
        />
      )}
      {deleteOpen && (
        <DeletePopup
          handleOnClose={closeDelete}
          title='الدولة'
          handleDelete={handleDelete}
          isLoading={isDeleting}
          selectedItem={deleteItem}
          description={deleteItem?.countryName_AR || ''}
        />
      )}{' '}
      {candidatesModalOpen && (
        <ManageCandidatesBatchesModal
          handleOnClose={closeCandidatesModal}
          onSave={handleSaveCandidatesBatches}
          // initialData={{ maleCandidates: 0, femaleCandidates: 0, totalBatches: 0 }} // optional
          // isLoading={false} // optional if you hook it to API
        />
      )}
    </div>
  );
};

export default StatisticsPage;
